import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Loader2, MapPin, Save } from "lucide-react";

const ALL_SUBJECTS = [
  "Matematyka",
  "Fizyka",
  "Chemia",
  "Biologia",
  "Geografia",
  "Historia",
  "Wiedza o społeczeństwie",
  "Język polski",
  "Język angielski",
  "Język niemiecki",
  "Język hiszpański",
  "Informatyka",
  "Wychowanie fizyczne",
  "Muzyka",
  "Plastyka",
  "Technika",
  "Religia",
  "Etyka",
  "Wychowanie przedszkolne",
  "Pedagogika",
  "Psychologia",
  "Logopedia",
];
const LEVELS = ["Przedszkole", "Podstawowa", "Liceum", "Technikum", "Branżowa", "Policealna"];

const Chip = ({ active, onClick, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
      active
        ? "bg-cyan-400/15 border-cyan-400/40 text-cyan-300"
        : "border-slate-700 text-slate-400 hover:border-slate-600 hover:text-slate-200"
    }`}
  >
    {children}
  </button>
);

export default function Profile() {
  const [p, setP] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/profile").then((r) => setP(r.data));
  }, []);

  if (!p) return <div className="p-12 text-slate-500">Ładowanie...</div>;

  const toggle = (key, val) => {
    const s = new Set(p[key] || []);
    s.has(val) ? s.delete(val) : s.add(val);
    setP({ ...p, [key]: Array.from(s) });
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", {
        address: p.address,
        max_radius_km: p.max_radius_km,
        subjects: p.subjects,
        levels: p.levels,
        positions: p.positions,
        employment_type: p.employment_type,
        digest_frequency: p.digest_frequency,
      });
      setP(data);
      if (data.lat && data.lng) {
        toast.success(`Zapisano. Adres znaleziono: ${data.lat.toFixed(3)}, ${data.lng.toFixed(3)}`);
      } else if (p.address) {
        toast.warning("Zapisano, ale nie udało się zgeokodować adresu. Sprawdź pisownię.");
      } else {
        toast.success("Zapisano profil");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-8 lg:p-12 max-w-4xl">
      <div className="mb-8">
        <div className="text-[11px] uppercase tracking-[0.25em] text-cyan-400 font-bold mb-2">Ustawienia użytkownika</div>
        <h1 className="font-display text-4xl font-semibold tracking-tight text-slate-50">Twój profil</h1>
        <p className="text-slate-400 mt-2 text-sm">Adres, promień km i predyspozycje decydują o dopasowaniu ofert.</p>
      </div>

      <div className="rounded-xl border border-slate-800 bg-slate-900/50 p-6 lg:p-8 space-y-8">
        {/* Address */}
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-400 flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" /> Adres bazowy
          </Label>
          <Input
            data-testid="profile-address-input"
            value={p.address || ""}
            onChange={(e) => setP({ ...p, address: e.target.value })}
            placeholder="np. ul. Marszałkowska 100, Warszawa"
            className="mt-2 h-11 bg-slate-950/60 border-slate-800 focus-visible:ring-cyan-400"
          />
          {p.lat != null && p.lng != null && (
            <div className="text-xs text-slate-500 mt-2" data-testid="profile-coords">
              Współrzędne: {p.lat.toFixed(4)}, {p.lng.toFixed(4)}
            </div>
          )}
        </div>

        {/* Radius */}
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">
            Promień poszukiwań · <span className="text-cyan-400 normal-case tracking-normal">{p.max_radius_km} km</span>
          </Label>
          <div className="mt-4 px-2">
            <Slider
              data-testid="profile-radius-slider"
              value={[p.max_radius_km]}
              min={1}
              max={200}
              step={1}
              onValueChange={(v) => setP({ ...p, max_radius_km: v[0] })}
            />
            <div className="flex justify-between text-[10px] text-slate-500 mt-2">
              <span>1 km</span>
              <span>200 km</span>
            </div>
          </div>
        </div>

        {/* Subjects */}
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Przedmioty</Label>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="profile-subjects">
            {ALL_SUBJECTS.map((s) => (
              <Chip
                key={s}
                testid={`subject-${s}`}
                active={(p.subjects || []).includes(s)}
                onClick={() => toggle("subjects", s)}
              >
                {s}
              </Chip>
            ))}
          </div>
        </div>

        {/* Levels */}
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Poziomy</Label>
          <div className="mt-3 flex flex-wrap gap-2" data-testid="profile-levels">
            {LEVELS.map((l) => (
              <Chip
                key={l}
                testid={`level-${l}`}
                active={(p.levels || []).includes(l)}
                onClick={() => toggle("levels", l)}
              >
                {l}
              </Chip>
            ))}
          </div>
        </div>

        {/* Positions */}
        <div>
          <Label className="text-xs uppercase tracking-[0.2em] text-slate-400">Stanowiska (słowa kluczowe)</Label>
          <Input
            data-testid="profile-positions-input"
            value={(p.positions || []).join(", ")}
            onChange={(e) =>
              setP({
                ...p,
                positions: e.target.value
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
            placeholder="np. nauczyciel, wychowawca, pedagog"
            className="mt-2 h-11 bg-slate-950/60 border-slate-800 focus-visible:ring-cyan-400"
          />
        </div>

        <div className="flex justify-end pt-4 border-t border-slate-800">
          <Button
            data-testid="profile-save-button"
            onClick={save}
            disabled={saving}
            className="h-11 px-6 bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-semibold"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            Zapisz profil
          </Button>
        </div>
      </div>
    </div>
  );
}
